<template>
  <!-- In-app manual, for the teacher and for the student: how each thing is
       done, with pictures of the screens, the notices that can show up and
       what the school has to set up. The texts are in locale app/help.json and
       the pictures in public/help (made with hermes-local/capture/manual-ayuda.mjs). -->
  <div class="help navigation-top-app">
    <navigation-top-app
      :app="app"
      :menu-screen="topic === null"
      :back="back"
    >
      <template v-slot:center>
        <div id="navigation-center">
          {{ topic ? topic.title : s.title }}
        </div>
      </template>
    </navigation-top-app>

    <div id="help-content">
      <template v-if="topic === null">
        <p class="help-intro">{{ s.intro[role] }}</p>
        <div class="help-topics">
          <button v-for="t in topics" :key="t.id" class="help-topic" v-on:click="open(t)">
            <span class="help-topic-icon"><font-awesome-icon :icon="icon(t.icon)" /></span>
            <span class="help-topic-text">
              <b>{{ t.title }}</b>
              <span>{{ t.summary }}</span>
            </span>
            <font-awesome-icon class="help-topic-go" :icon="['fas', 'chevron-right']" />
          </button>
        </div>
      </template>

      <template v-else>
        <p class="help-intro">{{ topic.summary }}</p>

        <section class="help-card">
          <h3>{{ s.steps }}</h3>
          <ol class="help-steps">
            <li v-for="(step, i) in topic.steps" :key="i" :class="{'with-image': step.image}">
              <span class="help-step-number">{{ i + 1 }}</span>
              <div class="help-step-body">
                <p>{{ step.text }}</p>
                <figure v-if="step.image" class="help-picture">
                  <img :src="'help/' + step.image + '.jpg'" :alt="s.picture" loading="lazy" />
                </figure>
              </div>
            </li>
          </ol>
        </section>

        <section class="help-card" v-if="topic.messages && topic.messages.length">
          <h3>{{ s.messages }}</h3>
          <div class="help-message" v-for="(m, i) in topic.messages" :key="i">
            <b>«{{ m.text }}»</b>
            <span>{{ m.meaning }}</span>
          </div>
        </section>

        <section class="help-card" v-if="topic.tips && topic.tips.length">
          <h3>{{ s.tips }}</h3>
          <ul class="help-tips">
            <li v-for="(tip, i) in topic.tips" :key="i">{{ tip }}</li>
          </ul>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
// Icons of the regular set; the rest are solid.
const REGULAR = ['calendar-alt', 'calendar-plus', 'file', 'clock']

export default {
  name: 'app-help-view',
  props: [
    'app'
  ],
  computed: {
    s () {
      return this.app.strings.app.help
    },
    role () {
      return this.app.user.role === 'teacher' ? 'teacher' : 'student'
    },
    topics () {
      return this.s[this.role]
    },
    // The open topic lives in the URL, so the phone's back button closes it.
    topic () {
      return this.topics.find((t) => t.id === this.$route.query.topic) || null
    }
  },
  methods: {
    icon (name) {
      return [REGULAR.indexOf(name) >= 0 ? 'far' : 'fas', name]
    },
    query (extra) {
      return Object.assign({origin: 'app', navigationLeft: 'false', token: this.app.token}, extra)
    },
    open (t) {
      this.$router.push({name: 'app.help', query: this.query({topic: t.id})}).catch(() => {})
      window.scrollTo(0, 0)
    },
    back () {
      if (this.topic) {
        this.$router.push({name: 'app.help', query: this.query({})}).catch(() => {})
      } else if (this.role === 'teacher') {
        this.$router.push({name: 'app.teacher.home', query: this.query({})})
      } else {
        this.$router.push({name: 'app.student.profile', params: {code: this.app.user.code}, query: this.query({})})
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/variables-app.scss";
@import "@/assets/styles/variables-valkiria.scss";

#help-content {
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
  padding: 72px 0 32px;
  box-sizing: border-box;
  color: $text-color-1;
}

.help-intro {
  margin: 4px 0 16px;
  color: $text-color-2;
  font-size: 15px;
  line-height: 1.5;
}

.help-topics {
  display: grid;
  gap: 10px;
}

.help-topic {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid $color-grey-light;
  border-radius: 18px;
  background-color: #FFFFFF;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.06);
  color: $text-color-1;
  font: inherit;
  text-align: left;
}

.help-topic-icon {
  display: flex;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  height: 44px;
  border-radius: 12px;
  background-color: #eaf3fb;
  color: $color-primary-1;
  font-size: 18px;
}

.help-topic-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.help-topic-text b {
  font-size: 15px;
}

.help-topic-text span {
  color: $text-color-2;
  font-size: 13px;
  line-height: 1.4;
}

.help-topic-go {
  flex: 0 0 auto;
  color: $text-color-3;
}

.help-card {
  margin-bottom: 16px;
  padding: 18px 16px;
  border: 1px solid $color-grey-light;
  border-radius: 20px;
  background-color: #FFFFFF;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.06);
  box-sizing: border-box;
}

.help-card h3 {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 700;
}

.help-steps {
  margin: 0;
  padding: 0;
  list-style: none;
}

.help-steps > li {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid $color-grey-light;
}

.help-steps > li:first-child {
  padding-top: 0;
  border-top: none;
}

.help-step-number {
  display: flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  height: 28px;
  border-radius: 999px;
  background-color: $color-primary-1;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 700;
}

.help-step-body {
  flex: 1;
  min-width: 0;
}

.help-step-body p {
  margin: 3px 0 0;
  font-size: 15px;
  line-height: 1.5;
}

.help-picture {
  margin: 12px 0 0;
}

.help-picture img {
  display: block;
  width: 100%;
  max-width: 280px;
  border: 1px solid $color-grey-light;
  border-radius: 16px;
  box-shadow: 0 8px 20px rgba(20, 67, 107, 0.08);
}

.help-message {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;
  border-top: 1px solid $color-grey-light;
  font-size: 14px;
  line-height: 1.45;
}

.help-message:first-of-type {
  padding-top: 0;
  border-top: none;
}

.help-message span {
  color: $text-color-2;
}

.help-tips {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.5;
}

// From tablet up the picture goes beside its step.
@media (min-width: 768px) {
  .help-topics {
    grid-template-columns: 1fr 1fr;
  }

  .help-steps > li.with-image .help-step-body {
    display: grid;
    grid-template-columns: 1fr 240px;
    gap: 20px;
    align-items: start;
  }

  .help-steps > li.with-image .help-picture {
    margin: 0;
  }
}

@media (min-width: 1024px) {
  #help-content {
    padding-top: 92px;
  }

  .help-card {
    padding: 24px;
  }
}
</style>
